import {
  FraudTrendOutcome,
  IListCheckCandidate,
  IListCheckEntry,
  IListCheckResolutionRequest
} from 'src/ts/interfaces/listCheck';
import { listCheckCopy } from '../components/ListChecksPanel/listCheckCopy';

export type ChipTone = 'good' | 'bad' | 'open' | 'neutral';

export interface ListCheckChip {
  tone: ChipTone;
  text: string;
}

export type ListCheckSection = 'needsReview' | 'reviewed' | 'cleared';

const MATCH_RULINGS = ['match', 'match_edited', 'match_p2pdf', 'match_correct'];

const ANSWER_TEXT: Record<string, string> = {
  no_match: 'No Match',
  match_correct: 'Match - Correct',
  match_mismatch: 'Mismatch',
  blocked: 'Edited',
  consistent: 'Not edited'
};

const NOT_RUN_REASONS: Record<string, string> = {
  timeout: 'timed out',
  engine_error: 'engine error',
  platform_error: 'platform error',
  check_disabled: 'check disabled',
  missing_input: 'missing input',
  low_confidence: 'low confidence',
  list_match: 'list match',
  sampled: 'sampled',
  multiple_matches: 'multiple matches'
};

const words = (value: string): string => value.replace(/_/g, ' ');

const sentence = (value: string): string => {
  const text = words(value);
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const humanizeNotRunReason = (reason: string | null): string =>
  reason ? NOT_RUN_REASONS[reason] ?? words(reason) : 'no reason given';

export const isMatchRuling = (ruling: string): boolean =>
  MATCH_RULINGS.includes(ruling) || ruling === 'match_mismatch';

export const isResolved = (entry: IListCheckEntry): boolean => entry.source === 'analyst';

export const fieldText = (value: unknown): string =>
  typeof value === 'string' ? value : JSON.stringify(value);

const textList = (value: unknown): string[] => (Array.isArray(value) ? value.map(fieldText) : []);

const isBlank = (value: unknown): boolean =>
  value == null || (Array.isArray(value) ? value.length === 0 : fieldText(value).trim() === '');

const sameValue = (documentValue: unknown, listValue: unknown): boolean =>
  Array.isArray(documentValue) || Array.isArray(listValue)
    ? fieldText(textList(documentValue).sort()) === fieldText(textList(listValue).sort())
    : fieldText(documentValue) === fieldText(listValue);

export type ListCheckComparisonKind = 'profile' | 'rule' | 'text' | 'registry';

export type ListCheckComparisonStatus =
  | 'differs'
  | 'match'
  | 'noMatch'
  | 'found'
  | 'notFound'
  | 'close'
  | 'unscored'
  | 'notOnList'
  | 'check';

export interface ListCheckComparisonRow {
  column: string;
  documentValue: string | null;
  listValue: string;
  status: ListCheckComparisonStatus;
  documentList?: string[];
  list?: string[];
}

export interface ListCheckComparison {
  kind: ListCheckComparisonKind;
  rows: ListCheckComparisonRow[];
  matchingColumns: string[];
  compared: number;
}

const COMPARISON_KINDS: Record<string, ListCheckComparisonKind> = {
  threat_metadata: 'rule',
  p2pdf_scan: 'rule',
  threat_companies: 'text',
  threat_elements: 'text',
  employer_payroll_registry: 'registry'
};

const PROFILE_COLUMNS = [
  'Application',
  'PDF Producer',
  'PDF Version',
  'Author',
  'Title',
  'fonts',
  'Encrypted (Y/N)',
  'Linearized (Y/N)',
  'Image count',
  'xref',
  'xrefc'
];

const REGISTRY_EMPLOYER = 'Paystubs Employer';
const REGISTRY_PROVIDER = 'Payroll Provider';

const squash = (value: string): string => value.toLowerCase().replace(/\s+/g, ' ').trim();

const employerFound = (listValue: string, documentValue: string): boolean => {
  const needle = squash(listValue);
  return needle.length > 0 && squash(documentValue).includes(needle);
};

const documentFieldsFor = (
  entry: IListCheckEntry,
  candidate: IListCheckCandidate
): Record<string, unknown> => candidate.document_fields ?? entry.document_fields ?? {};

const profileRow = (column: string, documentValue: unknown, listValue: unknown) => {
  const row: ListCheckComparisonRow = {
    column,
    documentValue: isBlank(documentValue) ? '' : fieldText(documentValue),
    listValue: isBlank(listValue) ? '' : fieldText(listValue),
    status: 'differs'
  };
  if (!Array.isArray(listValue) && !Array.isArray(documentValue)) return row;
  return { ...row, documentList: textList(documentValue), list: textList(listValue) };
};

const profileComparison = (
  fields: Record<string, unknown>,
  documentFields: Record<string, unknown>
): ListCheckComparison => {
  const read = PROFILE_COLUMNS.some((column) => !isBlank(documentFields[column]));
  const compared = read
    ? PROFILE_COLUMNS.filter(
        (column) => !isBlank(documentFields[column]) || !isBlank(fields[column])
      )
    : [];
  const matchingColumns = compared.filter((column) =>
    sameValue(documentFields[column], fields[column])
  );
  const rows = compared
    .filter((column) => !matchingColumns.includes(column))
    .map((column) => profileRow(column, documentFields[column], fields[column]));
  return { kind: 'profile', rows, matchingColumns, compared: compared.length };
};

const scoreStatus = (
  kind: 'rule' | 'text',
  entry: IListCheckEntry,
  score: number | undefined
): ListCheckComparisonStatus => {
  if (score == null || entry.match_bar == null || entry.floor == null) return 'unscored';
  if (score >= entry.match_bar) return kind === 'rule' ? 'match' : 'found';
  if (score >= entry.floor) return 'close';
  return kind === 'rule' ? 'noMatch' : 'notFound';
};

const valueRows = (
  kind: 'rule' | 'text',
  entry: IListCheckEntry,
  candidate: IListCheckCandidate,
  documentFields: Record<string, unknown>
): ListCheckComparisonRow[] => {
  const fields = candidate.fields ?? {};
  const scores = candidate.field_scores ?? {};
  const scored = (column: string) => documentFields[column] != null || scores[column] != null;
  const compared = (column: string): ListCheckComparisonRow => ({
    column,
    documentValue: documentFields[column] == null ? null : fieldText(documentFields[column]),
    listValue: fieldText(fields[column]),
    status: scoreStatus(kind, entry, scores[column])
  });
  if (kind === 'text' && entry.columns?.length && candidate.field_scores) {
    return entry.columns.map((column) =>
      isBlank(fields[column])
        ? { column, documentValue: '', listValue: '', status: 'notOnList' }
        : compared(column)
    );
  }
  return Object.keys(fields).filter(scored).map(compared);
};

const registryRows = (
  fields: Record<string, unknown>,
  documentFields: Record<string, unknown>
): ListCheckComparisonRow[] => {
  const rows: ListCheckComparisonRow[] = [];
  const employer = fields[REGISTRY_EMPLOYER];
  if (employer != null && documentFields[REGISTRY_EMPLOYER] != null) {
    const listValue = fieldText(employer);
    const documentValue = fieldText(documentFields[REGISTRY_EMPLOYER]);
    rows.push({
      column: REGISTRY_EMPLOYER,
      documentValue,
      listValue,
      status: employerFound(listValue, documentValue) ? 'found' : 'notFound'
    });
  }
  if (fields[REGISTRY_PROVIDER] != null) {
    rows.push({
      column: REGISTRY_PROVIDER,
      documentValue: null,
      listValue: fieldText(fields[REGISTRY_PROVIDER]),
      status: 'check'
    });
  }
  return rows;
};

export const listCheckComparison = (
  entry: IListCheckEntry,
  candidate: IListCheckCandidate
): ListCheckComparison | null => {
  const fields = candidate.fields ?? {};
  const documentFields = documentFieldsFor(entry, candidate);
  const kind = COMPARISON_KINDS[entry.check_key] ?? 'profile';
  if (kind === 'profile') {
    const comparison = profileComparison(fields, documentFields);
    return comparison.compared ? comparison : null;
  }
  const rows =
    kind === 'registry'
      ? registryRows(fields, documentFields)
      : valueRows(kind, entry, candidate, documentFields);
  const compared = rows.filter((row) => row.status !== 'notOnList').length;
  return rows.length ? { kind, rows, matchingColumns: [], compared } : null;
};

export const candidateFieldsLine = (candidate: IListCheckCandidate): string | null => {
  const parts = Object.entries(candidate.fields ?? {}).map(
    ([key, value]) => `${key}: ${fieldText(value)}`
  );
  return parts.length ? parts.join(' · ') : null;
};

const trendLabel = (entry: IListCheckEntry, ruling: 'blocked' | 'consistent'): string =>
  (isResolved(entry) ? undefined : entry.engine_labels?.[ruling]) ??
  entry.outcome_labels?.[ruling] ??
  ANSWER_TEXT[ruling];

const trendChip = (entry: IListCheckEntry): ListCheckChip | null => {
  if (entry.ruling === 'blocked') return { tone: 'bad', text: trendLabel(entry, 'blocked') };
  if (entry.ruling === 'consistent') {
    const tone = isResolved(entry) && entry.is_positive ? 'good' : 'neutral';
    return { tone, text: trendLabel(entry, 'consistent') };
  }
  if (entry.ruling === 'needs_review' && entry.ruling_reason === 'inconclusive') {
    return { tone: 'open', text: listCheckCopy.advancedFraudTrends.couldNotTell };
  }
  return null;
};

export const listCheckChip = (entry: IListCheckEntry): ListCheckChip | null => {
  if (entry.is_fraud_trend && entry.ruling !== 'not_run') return trendChip(entry);
  if (isResolved(entry)) {
    const text = ANSWER_TEXT[entry.ruling] ?? 'Match';
    if (entry.is_positive) return { tone: 'good', text };
    // Not being on an allow list or registry is not a bad signal, so it stays grey like the engine's No match.
    return { tone: entry.ruling === 'no_match' ? 'neutral' : 'bad', text };
  }
  if (entry.ruling === 'no_match') return { tone: 'neutral', text: 'No match' };
  if (entry.ruling === 'needs_review') return null;
  if (entry.ruling === 'not_run') return { tone: 'bad', text: 'Not run' };
  if (entry.ruling === 'match_mismatch') return { tone: 'bad', text: 'Mismatch' };
  if (MATCH_RULINGS.includes(entry.ruling)) {
    return { tone: entry.is_positive ? 'good' : 'bad', text: 'Potential match' };
  }
  return { tone: 'open', text: sentence(entry.ruling) };
};

export const listCheckSection = (entry: IListCheckEntry): ListCheckSection => {
  if (isResolved(entry)) return 'reviewed';
  if (entry.is_fraud_trend) return 'needsReview';
  return entry.ruling === 'no_match' ? 'cleared' : 'needsReview';
};

export const partitionListChecks = (
  entries: IListCheckEntry[]
): Record<ListCheckSection, IListCheckEntry[]> => ({
  needsReview: entries.filter((entry) => listCheckSection(entry) === 'needsReview'),
  reviewed: entries.filter((entry) => listCheckSection(entry) === 'reviewed'),
  cleared: entries.filter((entry) => listCheckSection(entry) === 'cleared')
});

export const trendAgreeEdits = (outcome: FraudTrendOutcome | null | undefined): boolean | null => {
  if (outcome === 'blocked') return true;
  if (outcome === 'consistent') return false;
  return null;
};

export const trendAgreed = (
  outcome: FraudTrendOutcome | null | undefined,
  edited: boolean
): boolean | null => {
  const agreeEdits = trendAgreeEdits(outcome);
  return agreeEdits === null ? null : agreeEdits === edited;
};

export const trendAnswerProps = (
  prior: IListCheckEntry | undefined,
  edited: boolean | null
): { kind?: 'fraud_trend'; template?: string; agreed?: boolean } => {
  if (!prior?.is_fraud_trend) return {};
  const agreed = edited === null ? null : trendAgreed(prior.outcome, edited);
  return {
    kind: 'fraud_trend',
    template: prior.template ?? 'unknown',
    ...(agreed !== null && { agreed })
  };
};

export const trendEdited = (
  resolution: IListCheckResolutionRequest['resolution'] | 'undo' | undefined
): boolean | null => {
  if (resolution === 'edited') return true;
  if (resolution === 'not_edited') return false;
  return null;
};
